import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { TranslatePipe } from '@ngx-translate/core';
import { ZagIcon } from '../icon/icon';
import { ZagButton } from '../button/button';

/**
 * A shift available for offering in a swap.
 * Mirrors MyAssignedShift but avoids cross-library dependency.
 */
export interface SwapOfferShift {
  /** Plan instance ID */
  planInstanceId: string;
  /** Plan name */
  planName: string;
  /** Team name */
  teamName: string;
  /** Shift unit ID */
  shiftUnitId: string;
  /** Shift name */
  shiftName: string;
  /** Shift color */
  shiftColor: string;
  /** Start time (HH:mm) */
  startTime: string;
  /** End time (HH:mm) */
  endTime: string;
  /** Date (YYYY-MM-DD) */
  date: string;
  /** Formatted date label */
  dateLabel: string;
  /** Whether the target employee is eligible for this shift (when available) */
  targetEligible?: boolean;
  /** Reason keys why the target employee is ineligible */
  targetIneligibilityReasons?: string[];
}

/**
 * Data passed to the SwapOfferDialog when opened.
 */
export interface SwapOfferDialogData {
  /** The target shift the user wants (for context display) */
  targetShiftName: string;
  /** The target shift color */
  targetShiftColor: string;
  /** The target shift date label */
  targetDateLabel: string;
  /** The target employee name (who holds the desired shift) */
  targetEmployeeName: string;
  /** The user's own assigned shifts to choose from */
  availableShifts: SwapOfferShift[];
}

/**
 * Result returned when the user selects a shift to offer.
 */
export interface SwapOfferDialogResult {
  /** The action taken */
  action: 'offer';
  /** The shift the user chose to offer */
  offeredShift: SwapOfferShift;
}

/**
 * Dialog that lets an employee pick which of their own assigned shifts
 * to offer in exchange when requesting a swap.
 *
 * @example
 * ```typescript
 * const dialogRef = this.dialog.open(SwapOfferDialogComponent, {
 *   data: { targetShiftName: 'Morning', availableShifts: [...] },
 *   panelClass: 'ai-chat-dialog-panel',
 *   backdropClass: 'ai-chat-dialog-backdrop',
 * });
 * ```
 */
@Component({
  selector: 'zag-swap-offer-dialog',
  imports: [TranslatePipe, ZagIcon, ZagButton],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'swap-offer-dialog-host' },
  template: `
    <div class="swap-offer-dialog">
      <div class="swap-offer-dialog__header">
        <h2 class="swap-offer-dialog__title">{{ 'shiftSwaps.offerDialog.title' | translate }}</h2>
        <button type="button" class="swap-offer-dialog__close" (click)="onClose()">
          <span zagIcon="close"></span>
        </button>
      </div>

      <div class="swap-offer-dialog__body">
        <p class="swap-offer-dialog__description">
          {{
            'shiftSwaps.offerDialog.description'
              | translate
                : {
                    shiftName: data.targetShiftName,
                    employeeName: data.targetEmployeeName,
                  }
          }}
        </p>

        @if (data.availableShifts.length === 0) {
          <div class="swap-offer-dialog__empty">
            <span zagIcon="event_busy" class="swap-offer-dialog__empty-icon"></span>
            <p class="swap-offer-dialog__empty-text">{{ 'shiftSwaps.offerDialog.noShifts' | translate }}</p>
          </div>
        } @else {
          <div class="swap-offer-dialog__list">
            @for (shift of data.availableShifts; track shift.shiftUnitId + '_' + shift.date) {
              <button
                class="swap-offer-dialog__item"
                [class.swap-offer-dialog__item--selected]="
                  selectedShift()?.shiftUnitId === shift.shiftUnitId && selectedShift()?.date === shift.date
                "
                (click)="onSelect(shift)"
              >
                <div class="swap-offer-dialog__item-bar" [style.background-color]="shift.shiftColor"></div>
                <div class="swap-offer-dialog__item-info">
                  <span class="swap-offer-dialog__item-name">{{ shift.shiftName }}</span>
                  <span class="swap-offer-dialog__item-time">{{ shift.startTime }} – {{ shift.endTime }}</span>
                </div>
                <div class="swap-offer-dialog__item-meta">
                  <span class="swap-offer-dialog__item-date">{{ shift.dateLabel }}</span>
                  <span class="swap-offer-dialog__item-team">{{ shift.teamName }}</span>
                </div>
                @if (selectedShift()?.shiftUnitId === shift.shiftUnitId && selectedShift()?.date === shift.date) {
                  <span zagIcon="check_circle" class="swap-offer-dialog__item-check"></span>
                }
              </button>
            }
          </div>
        }
      </div>

      @if (data.availableShifts.length > 0) {
        <div class="swap-offer-dialog__footer">
          <button zagButton variant="outlined" (click)="onClose()">
            {{ 'common.cancel' | translate }}
          </button>
          <button zagButton [disabled]="!selectedShift()" (click)="onConfirm()">
            <span zagIcon="swap_horiz"></span>
            {{ 'shiftSwaps.offerDialog.confirm' | translate }}
          </button>
        </div>
      }
    </div>
  `,
  styles: [
    `
      .swap-offer-dialog {
        background: var(--surface-color-default);
        border-radius: var(--border-radius-lg);
        padding: var(--spacing-lg);
        max-width: 520px;
        max-height: 80vh;
        display: flex;
        flex-direction: column;
        box-shadow: var(--shadow-lg);
      }

      .swap-offer-dialog__header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: var(--spacing-md);
        flex-shrink: 0;
      }

      .swap-offer-dialog__title {
        margin: 0;
        font-size: var(--font-size-lg);
        font-weight: var(--font-weight-semibold);
        color: var(--text-color-primary);
      }

      .swap-offer-dialog__close {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        border: none;
        background: transparent;
        color: var(--text-color-secondary);
        cursor: pointer;
        border-radius: var(--border-radius-sm);
        transition: background-color 0.15s;
      }

      .swap-offer-dialog__close:hover {
        background: var(--surface-color-hover);
      }

      .swap-offer-dialog__body {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-md);
        overflow-y: auto;
        flex: 1;
        min-height: 0;
      }

      .swap-offer-dialog__description {
        margin: 0;
        font-size: var(--font-size-sm);
        color: var(--text-color-secondary);
        line-height: 1.5;
      }

      .swap-offer-dialog__empty {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: var(--spacing-sm);
        padding: var(--spacing-xl) 0;
        color: var(--text-color-tertiary);
      }

      .swap-offer-dialog__empty-icon {
        font-size: 40px;
      }

      .swap-offer-dialog__empty-text {
        margin: 0;
        font-size: var(--font-size-sm);
      }

      .swap-offer-dialog__list {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-xs);
      }

      .swap-offer-dialog__item {
        display: flex;
        align-items: center;
        gap: var(--spacing-sm);
        padding: var(--spacing-sm) var(--spacing-md);
        background: var(--surface-color-default);
        border: 2px solid var(--border-color-default);
        border-radius: var(--border-radius-md);
        cursor: pointer;
        text-align: left;
        transition:
          background-color 0.15s,
          border-color 0.15s;
      }

      .swap-offer-dialog__item:hover {
        background: var(--surface-color-hover);
        border-color: var(--border-color-hover);
      }

      .swap-offer-dialog__item--selected {
        border-color: var(--color-primary);
        background: color-mix(in srgb, var(--color-primary) 5%, transparent);
      }

      .swap-offer-dialog__item-bar {
        width: 4px;
        height: 32px;
        border-radius: 2px;
        flex-shrink: 0;
      }

      .swap-offer-dialog__item-info {
        display: flex;
        flex-direction: column;
        gap: 2px;
        min-width: 100px;
      }

      .swap-offer-dialog__item-name {
        font-weight: 500;
        color: var(--text-color-primary);
        font-size: var(--font-size-md);
      }

      .swap-offer-dialog__item-time {
        font-size: var(--font-size-sm);
        color: var(--text-color-secondary);
        font-family: monospace;
      }

      .swap-offer-dialog__item-meta {
        display: flex;
        flex-direction: column;
        gap: 2px;
        flex: 1;
        min-width: 0;
      }

      .swap-offer-dialog__item-date {
        font-size: var(--font-size-sm);
        color: var(--text-color-secondary);
      }

      .swap-offer-dialog__item-team {
        font-size: var(--font-size-xs);
        color: var(--text-color-tertiary);
      }

      .swap-offer-dialog__item-check {
        color: var(--color-primary);
        font-size: 22px;
        flex-shrink: 0;
      }

      .swap-offer-dialog__footer {
        display: flex;
        gap: var(--spacing-sm);
        justify-content: flex-end;
        padding-top: var(--spacing-md);
        border-top: 1px solid var(--border-color-default);
        margin-top: var(--spacing-md);
        flex-shrink: 0;
      }
    `,
  ],
})
export class SwapOfferDialogComponent {
  private readonly dialogRef = inject(DialogRef<SwapOfferDialogResult>);
  readonly data = inject<SwapOfferDialogData>(DIALOG_DATA);

  /** Currently selected shift */
  readonly selectedShift = signal<SwapOfferShift | null>(null);

  onSelect(shift: SwapOfferShift): void {
    const current = this.selectedShift();
    if (current?.shiftUnitId === shift.shiftUnitId && current?.date === shift.date) {
      this.selectedShift.set(null);
    } else {
      this.selectedShift.set(shift);
    }
  }

  onConfirm(): void {
    const shift = this.selectedShift();
    if (shift) {
      this.dialogRef.close({ action: 'offer', offeredShift: shift });
    }
  }

  onClose(): void {
    this.dialogRef.close();
  }
}
